import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { calculators, categories, getCalculatorsByCategory } from '../data/calculators';
import Navbar from '../components/common/Navbar';

const LandingPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredCalculators = useMemo(() => {
    if (activeCategory !== 'all') {
      return getCalculatorsByCategory(activeCategory);
    }
    return calculators;
  }, [activeCategory]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0c3528 0%, #1a5e44 38%, #3a9468 68%, #68c090 100%)',
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Onest:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />

      <Navbar />

      {/* Main Content */}
      <div className="landing-content">

        {/* Breadcrumb */}
        <div style={{ marginBottom: '18px' }}>
          <Link to="/" style={{
            fontFamily: 'Onest, sans-serif',
            fontSize: '13px',
            color: 'rgba(255,255,255,0.75)',
            textDecoration: 'none',
            fontWeight: 400,
          }}>Home</Link>
          <span style={{
            fontFamily: 'Onest, sans-serif',
            fontSize: '13px',
            color: 'rgba(255,255,255,0.45)',
            margin: '0 6px',
          }}>/</span>
          <span style={{
            fontFamily: 'Onest, sans-serif',
            fontSize: '13px',
            color: 'rgba(255,255,255,0.45)',
          }}>Calculators</span>
        </div>

        {/* Title */}
        <h1
          className="landing-title"
          style={{
            fontFamily: 'Instrument Serif, serif',
            fontWeight: 400,
            fontSize: '62px',
            color: '#ffffff',
            margin: '0 0 22px 0',
            lineHeight: 1,
          }}
        >
          Calculators
        </h1>

        {/* Category Pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '32px' }}>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              style={{
                fontFamily: 'Onest, sans-serif',
                fontSize: '14px',
                padding: '8px 18px',
                borderRadius: '50px',
                fontWeight: activeCategory === cat.id ? 600 : 400,
                cursor: 'pointer',
                backgroundColor: activeCategory === cat.id ? '#ffffff' : 'rgba(0,0,0,0.22)',
                color: activeCategory === cat.id ? '#0c3528' : 'rgba(255,255,255,0.85)',
                border: activeCategory === cat.id ? 'none' : '1px solid rgba(255,255,255,0.18)',
                outline: 'none',
                transition: 'all 0.15s ease',
              }}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Calculator Cards Grid */}
        <div className="calc-grid">
          {filteredCalculators.map((calc) => (
            <Link
              key={calc.id}
              to={"/calculators/" + calc.slug}
              style={{ textDecoration: 'none' }}
            >
              <div
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '20px',
                  padding: '20px 20px 48px 20px',
                  position: 'relative',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  boxShadow: '0 2px 20px rgba(0,0,0,0.1)',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  minHeight: calc.image ? '230px' : 'auto',
                  height: '100%',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 28px rgba(0,0,0,0.14)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 20px rgba(0,0,0,0.1)';
                }}
              >
                {/* Image — behind text, anchored to bottom-right, fades in from left edge */}
                {calc.image && (
                  <img
                    src={calc.image}
                    alt=""
                    fetchPriority="high"
                    style={{
                      position: 'absolute',
                      bottom: '-2px',
                      right: '-2px',
                      width: '55%',
                      height: 'auto',
                      objectFit: 'contain',
                      zIndex: 0,
                      WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 28%)',
                      maskImage: 'linear-gradient(to right, transparent 0%, black 28%)',
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}

                {/* Title */}
                <h3
                  style={{
                    fontFamily: 'Onest, sans-serif',
                    fontWeight: 700,
                    fontSize: '18px',
                    color: '#115e59',
                    marginBottom: '6px',
                    marginTop: 0,
                    lineHeight: 1.2,
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {calc.name}
                </h3>

                {/* Description — clamped to 2 lines */}
                <p
                  style={{
                    fontFamily: 'Onest, sans-serif',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#9ca3af',
                    lineHeight: 1.55,
                    margin: 0,
                    position: 'relative',
                    zIndex: 1,
                    maxWidth: calc.image ? '52%' : '100%',
                  }}
                >
                  {calc.description}
                </p>

                {/* Popular Badge */}
                {calc.popular && (
                  <span
                    style={{
                      fontFamily: 'Onest, sans-serif',
                      fontSize: '12px',
                      fontWeight: 600,
                      backgroundColor: '#0c3528',
                      color: '#4ade80',
                      padding: '5px 14px',
                      borderRadius: '50px',
                      position: 'absolute',
                      bottom: '14px',
                      left: '18px',
                      zIndex: 1,
                    }}
                  >
                    Popular
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>

        {/* No Results */}
        {filteredCalculators.length === 0 && (
          <div style={{ textAlign: 'center', padding: '64px 0' }}>
            <p style={{
              fontFamily: 'Onest, sans-serif',
              fontSize: '15px',
              color: 'rgba(255,255,255,0.6)',
            }}>
              No calculators found in this category
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LandingPage;
